import React, { ForwardedRef } from "react";
import { Link } from "react-router-dom";
import { getUrl } from "../../../helpers/getUrl.helper";

interface DA_BasicLinkProps {
    href: string;
    target?: "_blank" | "_self";
    children: React.ReactNode;
    className?: string;
    ariaLabel?: string;
    title?: string;
    itemScope?: boolean;
    itemProp?: string;
    itemType?: string;
    itemID?: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onMouseEnter?: () => void;
    conventionalRouting?: boolean;
    id?: string;
    forceNativeUrl?: boolean;
}

export const DA_BasicLink = React.forwardRef(
    (
        {
            href,
            target,
            children,
            className,
            ariaLabel,
            title,
            itemScope,
            itemProp,
            itemType,
            itemID,
            onClick,
            onMouseEnter,
            conventionalRouting,
            id,
            forceNativeUrl,
        }: DA_BasicLinkProps,
        ref: ForwardedRef<HTMLAnchorElement>
    ): JSX.Element => {
        const hrefLink = href?.startsWith("link:") ? getUrl(href.split(":")?.[1]) || "#" : href;

        // console.log('hrefLink', hrefLink);

        const isExternal = /^https?:\/\/|^mailto:|^tel:/.test(hrefLink);

        // Use native link (<a>...</a>) for external links, if conventionalRouting is true,
        // and for certain pages where the backend requires a real page request
        const useNativeLink =
            isExternal || conventionalRouting || /^\/(login|logout)($|\?|#)/.test(hrefLink);

        //Get currentculture to the hrefLang
        const currentCulture = globalThis?.dictionaryObj?.culture
            ? globalThis?.dictionaryObj.culture.substring(0, 2)
            : null;

        const linkProp = {
            target: target,
            rel: isExternal ? "noopener noreferrer" : "alternate",
            className: className,
            "aria-label": ariaLabel,
            title: title,
            itemScope: itemScope,
            itemProp: itemProp,
            itemType: itemType,
            itemID: itemID,
            onClick: onClick,
            onMouseEnter: onMouseEnter,
            hrefLang: currentCulture,
            id: id,
        };

        return useNativeLink || forceNativeUrl ? (
            <a href={hrefLink} {...linkProp} ref={ref}>
                {children}
            </a>
        ) : (
            <Link to={hrefLink} {...linkProp} ref={ref}>
                {children}
            </Link>
        );
    }
);

DA_BasicLink.displayName = "DA_BasicLink";
